import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { canApproveFinancialRequests, canDeleteFinancialRequests, canManageFinancialLedger } from "@/lib/permissions";
import { Check, Download, FileText, Plus, Trash2, Upload, Wallet, X } from "lucide-react";

const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const emptyRequest = (name = "") => ({
  name,
  request_date: "",
  amount: "",
  purpose: "",
  attachment: "",
  attachment_name: "",
});
const emptyTransaction = { transaction_date: "", amount: "", purpose: "", remark: "" };

const money = (value) => new Intl.NumberFormat("en-MY", {
  style: "currency",
  currency: "MYR",
}).format(Number(value || 0));

const dateLabel = (value) => {
  if (!value) return "-";
  return new Intl.DateTimeFormat("en-MY", { day: "numeric", month: "short", year: "numeric" })
    .format(new Date(`${value}T00:00:00`));
};

export default function Financial() {
  const { user } = useAuth();
  const showLedger = canManageFinancialLedger(user);
  const canApproveRequests = canApproveFinancialRequests(user);
  const canDeleteRequests = canDeleteFinancialRequests(user);
  const [section, setSection] = useState("claim");
  const [requests, setRequests] = useState([]);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestType, setRequestType] = useState("claim");
  const [requestForm, setRequestForm] = useState(emptyRequest(user?.name));
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [ledger, setLedger] = useState({ transactions: [], balance: 0 });
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [transactionType, setTransactionType] = useState("income");
  const [transactionForm, setTransactionForm] = useState(emptyTransaction);
  const [submittingTransaction, setSubmittingTransaction] = useState(false);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const response = await api.get("/financial/requests");
        setRequests(response.data || []);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Unable to load financial requests");
      }
      if (showLedger) {
        try {
          const response = await api.get("/financial/transactions");
          setLedger(response.data || { transactions: [], balance: 0 });
        } catch (error) {
          toast.error(error.response?.data?.detail || "Unable to load financial ledger");
        }
      }
    };
    load();
  }, [user, showLedger]);

  const loadRequests = async () => {
    const response = await api.get("/financial/requests");
    setRequests(response.data || []);
  };

  const loadLedger = async () => {
    const response = await api.get("/financial/transactions");
    setLedger(response.data || { transactions: [], balance: 0 });
  };

  const updateRequestStatus = async (request, status) => {
    try {
      const justification = status === "Rejected" ? window.prompt("Please provide a rejection justification for the submitter.", "") : "";
      if (status === "Rejected" && (!justification || !justification.trim())) {
        toast.error("A rejection justification is required");
        return;
      }
      await api.put(`/financial/requests/${request.id}`, { status, justification: justification?.trim() || "" });
      await loadRequests();
      toast.success(`Request ${status.toLowerCase()}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to update request");
    }
  };

  const deleteRequest = async (request) => {
    if (!window.confirm(`Delete this ${request.request_type === "claim" ? "claim" : "fund request"}? This cannot be undone.`)) return;
    try {
      await api.delete(`/financial/requests/${request.id}`);
      await loadRequests();
      toast.success("Request deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to delete request");
    }
  };

  const downloadAttachment = async (request) => {
    try {
      const response = await api.get(`/financial/requests/${request.id}/attachment`, { responseType: "blob" });
      const fileUrl = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = request.attachment_name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(fileUrl);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to download attachment");
    }
  };

  const chooseAttachment = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_ATTACHMENT_BYTES) {
      event.target.value = "";
      toast.error("File must not exceed 5 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setRequestForm((current) => ({
      ...current,
      attachment: reader.result,
      attachment_name: file.name,
    }));
    reader.onerror = () => toast.error("Unable to read this file");
    reader.readAsDataURL(file);
  };

  const openRequestForm = (type) => {
    setRequestType(type);
    setRequestForm(emptyRequest(user?.name || ""));
    setRequestDialogOpen(true);
  };

  const submitRequest = async (event) => {
    event.preventDefault();
    setSubmittingRequest(true);
    try {
      await api.post("/financial/requests", {
        ...requestForm,
        request_type: requestType,
        amount: Number(requestForm.amount),
      });
      await loadRequests();
      setRequestDialogOpen(false);
      setRequestForm(emptyRequest(user?.name || ""));
      toast.success(requestType === "claim" ? "Claim submitted" : "Fund request submitted");
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || "Unable to submit request";
      toast.error(detail);
    } finally {
      setSubmittingRequest(false);
    }
  };

  const openTransactionForm = (type) => {
    setTransactionType(type);
    setTransactionForm(emptyTransaction);
    setTransactionDialogOpen(true);
  };

  const submitTransaction = async (event) => {
    event.preventDefault();
    setSubmittingTransaction(true);
    try {
      await api.post("/financial/transactions", {
        ...transactionForm,
        transaction_type: transactionType,
        amount: Number(transactionForm.amount),
        remark: transactionForm.remark || "",
      });
      await loadLedger();
      setTransactionDialogOpen(false);
      setTransactionForm(emptyTransaction);
      toast.success(transactionType === "income" ? "Income added" : "Expenditure added");
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || "Unable to save transaction";
      toast.error(detail);
    } finally {
      setSubmittingTransaction(false);
    }
  };

  const deleteTransaction = async (transaction) => {
    if (!window.confirm(`Delete this ${transaction.transaction_type} record?`)) return;
    try {
      await api.delete(`/financial/transactions/${transaction.id}`);
      await loadLedger();
      toast.success("Transaction deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to delete transaction");
    }
  };

  const clearAllTransactions = async () => {
    if (!window.confirm("Clear all income and expenditure records? This will reset the ledger balance.")) return;
    try {
      await api.delete("/financial/transactions");
      await loadLedger();
      toast.success("Ledger cleared");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to clear ledger");
    }
  };

  const exportTransactions = async () => {
    try {
      const response = await api.get("/financial/transactions/export", { responseType: "blob" });
      const fileUrl = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = "financial-ledger.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(fileUrl);
      toast.success("Ledger CSV downloaded");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to download ledger CSV");
    }
  };

  const renderRequests = (type) => {
    const filtered = requests.filter((request) => request.request_type === type);
    if (!filtered.length) {
      return <p className="border-t border-border py-8 text-center text-sm text-muted-foreground">No {type === "claim" ? "claims" : "fund requests"} submitted yet.</p>;
    }
    return (
      <div className="overflow-x-auto border-t border-border">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead className="text-xs uppercase text-muted-foreground">
            <tr>
              <th className="py-3 pr-4 font-medium">Ref No</th>
              <th className="py-3 pr-4 font-medium">Name</th>
              <th className="py-3 pr-4 font-medium">{type === "claim" ? "Transaction date" : "Date needed"}</th>
              <th className="py-3 pr-4 font-medium">Amount</th>
              <th className="py-3 pr-4 font-medium">Purpose</th>
              <th className="py-3 pr-4 font-medium">Attachment</th>
              <th className="py-3 font-medium">Status</th>
              {(canApproveRequests || canDeleteRequests) && <th className="py-3 pl-4 font-medium">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map((request) => (
              <tr key={request.id} className="border-t border-border/70 align-top" data-testid={`financial-request-${request.id}`}>
                <td className="py-3 pr-4 font-medium whitespace-nowrap">{request.reference_number || request.ref_no || request.id}</td>
                <td className="py-3 pr-4 font-medium">
                  {request.name}
                  {request.requester_name && <span className="mt-1 block text-xs font-normal text-muted-foreground">Submitted by {request.requester_name}</span>}
                </td>
                <td className="py-3 pr-4 whitespace-nowrap">{dateLabel(request.request_date)}</td>
                <td className="py-3 pr-4 whitespace-nowrap">{money(request.amount)}</td>
                <td className="max-w-xs py-3 pr-4">{request.purpose}</td>
                <td className="py-3 pr-4">
                  <Button type="button" size="sm" variant="ghost" onClick={() => downloadAttachment(request)} data-testid={`financial-request-attachment-${request.id}`}>
                    <Download className="mr-1 h-4 w-4" /><FileText className="h-4 w-4" />{request.attachment_name}
                  </Button>
                </td>
                <td className="py-3">
                  <Badge variant="secondary">{request.status}</Badge>
                  {request.status === "Rejected" && (request.review_justification || request.rejection_justification || request.justification) && (
                    <div className="mt-2 max-w-xs rounded-md border border-destructive/20 bg-destructive/5 p-2 text-xs text-destructive">
                      <span className="mb-1 block font-semibold uppercase tracking-wide">Justification</span>
                      {request.review_justification || request.rejection_justification || request.justification}
                    </div>
                  )}
                </td>
                {(canApproveRequests || canDeleteRequests) && (
                  <td className="py-3 pl-4">
                    <div className="flex flex-wrap gap-2">
                      {canApproveRequests && request.status === "Submitted" && (
                        <>
                          <Button size="sm" onClick={() => updateRequestStatus(request, "Approved")} data-testid={`financial-request-approve-${request.id}`}><Check className="mr-1 h-4 w-4" />Approve</Button>
                          <Button size="sm" variant="destructive" onClick={() => updateRequestStatus(request, "Rejected")} data-testid={`financial-request-reject-${request.id}`}><X className="mr-1 h-4 w-4" />Reject</Button>
                        </>
                      )}
                      {canDeleteRequests && ["Approved", "Rejected"].includes(request.status) && (
                        <Button size="sm" variant="ghost" onClick={() => deleteRequest(request)} data-testid={`financial-request-delete-${request.id}`}><Trash2 className="mr-1 h-4 w-4" />Delete</Button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const pageHeading = section === "claim" ? "Claim Submission" : section === "fund" ? "Fund Request" : "Financial Ledger";

  return (
    <div className="space-y-7" data-testid="financial-page">
      <header className="border-b border-border pb-5">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Finance</p>
        <h1 className="mt-1 flex items-center gap-3 font-display text-3xl font-extrabold"><Wallet className="h-8 w-8 text-primary" />Financial</h1>
      </header>

      <Tabs value={section} onValueChange={setSection}>
        <TabsList data-testid="financial-sections">
          <TabsTrigger value="claim" data-testid="financial-claim-tab">Claim Submission</TabsTrigger>
          <TabsTrigger value="fund" data-testid="financial-fund-tab">Fund Request</TabsTrigger>
          {showLedger && <TabsTrigger value="ledger" data-testid="financial-ledger-tab">Ledger</TabsTrigger>}
        </TabsList>
      </Tabs>

      {section !== "ledger" ? (
        <section className="space-y-4 border-t-4 border-sky-500 pt-4" aria-labelledby="financial-section-title">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 id="financial-section-title" className="font-display text-2xl font-bold">{pageHeading}</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {section === "claim" ? "Submit a claim with its transaction details and receipt." : "Request funds with the required date, purpose, and budget paperwork."}
              </p>
            </div>
            <Button onClick={() => openRequestForm(section)} data-testid={section === "claim" ? "financial-new-claim" : "financial-new-fund-request"}>
              <Plus className="mr-2 h-4 w-4" />{section === "claim" ? "New claim" : "New fund request"}
            </Button>
          </div>
          {renderRequests(section)}
        </section>
      ) : showLedger ? (
        <section className="space-y-5 border-t-4 border-emerald-500 pt-4" aria-labelledby="financial-ledger-title" data-testid="financial-ledger-section">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 id="financial-ledger-title" className="font-display text-2xl font-bold">Ledger</h2>
              <p className="mt-1 text-sm text-muted-foreground">Record transactions and track the running balance.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={exportTransactions} data-testid="financial-export-ledger-csv"><Download className="mr-2 h-4 w-4" />Download CSV</Button>
              {canManageFinancialLedger(user) && (
                <Button variant="destructive" onClick={clearAllTransactions} data-testid="financial-clear-ledger"><Trash2 className="mr-2 h-4 w-4" />Clear all</Button>
              )}
              <Button onClick={() => openTransactionForm("income")} data-testid="financial-add-income"><Plus className="mr-2 h-4 w-4" />Add income</Button>
              <Button variant="secondary" onClick={() => openTransactionForm("expenditure")} data-testid="financial-add-expenditure"><Plus className="mr-2 h-4 w-4" />Add expenditure</Button>
            </div>
          </div>
          <div className="border-y border-border py-4">
            <p className="text-xs font-semibold uppercase text-muted-foreground">Current balance</p>
            <p className="mt-1 font-display text-3xl font-bold" data-testid="financial-current-balance">{money(ledger.balance)}</p>
          </div>
          {ledger.transactions.length ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[620px] text-left text-sm" data-testid="financial-ledger-table">
                <thead className="text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="py-3 pr-4 font-medium">Date</th>
                    <th className="py-3 pr-4 font-medium">Ref No</th>
                    <th className="py-3 pr-4 font-medium">Income</th>
                    <th className="py-3 pr-4 font-medium">Expenditure</th>
                    <th className="py-3 pr-4 font-medium">Purpose</th>
                    <th className="py-3 pr-4 font-medium">Remark</th>
                    <th className="py-3 pr-4 font-medium">Balance</th>
                    {canDeleteRequests && <th className="py-3 font-medium">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {ledger.transactions.slice().reverse().map((transaction) => (
                    <tr key={transaction.id} className="border-t border-border/70" data-testid={`financial-transaction-${transaction.id}`}>
                      <td className="py-3 pr-4 whitespace-nowrap">{dateLabel(transaction.transaction_date)}</td>
                      <td className="py-3 pr-4 whitespace-nowrap font-medium">{transaction.reference_number || transaction.ref_no || transaction.id}</td>
                      <td className="py-3 pr-4 whitespace-nowrap text-emerald-700">{transaction.transaction_type === "income" ? money(transaction.amount) : "-"}</td>
                      <td className="py-3 pr-4 whitespace-nowrap text-destructive">{transaction.transaction_type === "expenditure" ? money(transaction.amount) : "-"}</td>
                      <td className="py-3 pr-4">{transaction.purpose}</td>
                      <td className="py-3 pr-4">{transaction.remark || transaction.remarks || "-"}</td>
                      <td className="py-3 pr-4 font-medium whitespace-nowrap">{money(transaction.balance_after)}</td>
                      {canDeleteRequests && (
                        <td className="py-3">
                          <Button size="sm" variant="ghost" onClick={() => deleteTransaction(transaction)} data-testid={`financial-transaction-delete-${transaction.id}`}><Trash2 className="mr-1 h-4 w-4" />Delete</Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">No income or expenditure recorded yet.</p>
          )}
        </section>
      ) : null}

      <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{requestType === "claim" ? "Claim request form" : "Fund request form"}</DialogTitle>
            <DialogDescription>{requestType === "claim" ? "Provide the transaction details and attach the receipt." : "Provide the requested amount and attach the budget paperwork."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitRequest} className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="financial-request-name">Name</Label>
              <Input id="financial-request-name" required maxLength={120} value={requestForm.name} onChange={(event) => setRequestForm({ ...requestForm, name: event.target.value })} data-testid="financial-request-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="financial-request-date">{requestType === "claim" ? "Transaction date" : "Date needed"}</Label>
              <Input id="financial-request-date" type="date" required value={requestForm.request_date} onChange={(event) => setRequestForm({ ...requestForm, request_date: event.target.value })} data-testid="financial-request-date-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="financial-request-amount">{requestType === "claim" ? "Amount" : "Amount needed (MYR)"}</Label>
              <Input id="financial-request-amount" type="number" min="0.01" step="0.01" required value={requestForm.amount} onChange={(event) => setRequestForm({ ...requestForm, amount: event.target.value })} data-testid="financial-request-amount-input" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="financial-request-purpose">Purpose</Label>
              <Textarea id="financial-request-purpose" required maxLength={2000} rows={3} value={requestForm.purpose} onChange={(event) => setRequestForm({ ...requestForm, purpose: event.target.value })} data-testid="financial-request-purpose-input" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="financial-request-attachment">{requestType === "claim" ? "Receipt" : "Budget paperwork"}</Label>
              <Input id="financial-request-attachment" type="file" required accept="application/pdf,image/png,image/jpeg,image/webp,image/gif" onChange={chooseAttachment} data-testid="financial-request-attachment-input" />
              <p className="text-xs text-muted-foreground">PDF or image, max 5 MB.</p>
              {requestForm.attachment_name && <p className="flex items-center gap-2 text-sm"><Upload className="h-4 w-4" />{requestForm.attachment_name}</p>}
            </div>
            <div className="flex justify-end gap-2 sm:col-span-2">
              <Button type="button" variant="secondary" onClick={() => setRequestDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submittingRequest} data-testid="financial-request-submit">{submittingRequest ? "Submitting…" : "Submit request"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={transactionDialogOpen} onOpenChange={setTransactionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add {transactionType}</DialogTitle>
            <DialogDescription>Enter the transaction details. The ledger balance updates automatically.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitTransaction} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="financial-transaction-date">Date</Label>
              <Input id="financial-transaction-date" type="date" required value={transactionForm.transaction_date} onChange={(event) => setTransactionForm({ ...transactionForm, transaction_date: event.target.value })} data-testid="financial-transaction-date-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="financial-transaction-amount">Amount (MYR)</Label>
              <Input id="financial-transaction-amount" type="number" min="0.01" step="0.01" required value={transactionForm.amount} onChange={(event) => setTransactionForm({ ...transactionForm, amount: event.target.value })} data-testid="financial-transaction-amount-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="financial-transaction-purpose">Purpose</Label>
              <Textarea id="financial-transaction-purpose" required maxLength={2000} rows={3} value={transactionForm.purpose} onChange={(event) => setTransactionForm({ ...transactionForm, purpose: event.target.value })} data-testid="financial-transaction-purpose-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="financial-transaction-remark">Remark</Label>
              <Textarea id="financial-transaction-remark" maxLength={2000} rows={3} value={transactionForm.remark} onChange={(event) => setTransactionForm({ ...transactionForm, remark: event.target.value })} data-testid="financial-transaction-remark-input" placeholder="Optional remarks for this transaction" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setTransactionDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submittingTransaction} data-testid="financial-transaction-submit">{submittingTransaction ? "Saving…" : `Add ${transactionType}`}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}